import React, { useCallback, useEffect, useMemo } from "react";

import { IQueryOptions } from "api/api-hooks/api-hooks-common";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { T } from "services/lang/LanguageService";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import {
  ChartDataset,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { AdvancedProps, Datapoint, LineConfig, ValueLabel } from "../sparkline/SparkLineTypes";
import "./SparkLineChart.scss";
import "chartjs-adapter-date-fns";

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Filler);

type SparkLineChartProps = {
  dataset: string;
  dataArray: number[];
  advancedProps: AdvancedProps;
  lineColor?: string;
  lineWidth?: number;
  chartWidth?: number;
  chartHeight?: number;
  setValueLabel: React.Dispatch<React.SetStateAction<ValueLabel>>;
  minYAxis?: number;
  maxYAxis?: number;
  isInlineMultipleCharts?: boolean;
};

const options = (
  showYAxis = false,
  minYAxis?: number,
  maxYAxis?: number
): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 0,
  },
  plugins: {
    tooltip: {
      enabled: false,
    },
    legend: {
      display: false,
    },
  },
  interaction: {
    intersect: false,
  },
  scales: {
    x: {
      display: false,
      type: "time",
    },
    y: {
      display: showYAxis && "auto",
      min: minYAxis,
      max: maxYAxis,
    },
  },
});

const SparkLineChart: React.FC<SparkLineChartProps> = ({
  dataset,
  dataArray,
  advancedProps,
  lineColor,
  lineWidth,
  chartWidth,
  chartHeight,
  setValueLabel,
  minYAxis,
  maxYAxis,
  isInlineMultipleCharts,
}) => {
  const startDate = useMemo(
    () => (advancedProps.fromDate ? new Date(advancedProps.fromDate) : new Date()),
    [advancedProps.fromDate]
  );
  const endDate = useMemo(
    () => (advancedProps.toDate ? new Date(advancedProps.toDate) : new Date()),
    [advancedProps.toDate]
  );

  const hasDuplicatedDatasets = useMemo(() => {
    const datasetIds = [
      dataset,
      advancedProps.baseline?.dataset,
      advancedProps.minLine?.dataset,
      advancedProps.maxLine?.dataset,
    ].filter(Boolean);

    const uniqueSet = new Set(datasetIds);
    return uniqueSet.size !== datasetIds.length;
  }, [advancedProps]);

  const queryParams = useCallback(
    (
      identifier?: string,
      isDatasetUsage?: boolean
    ): [string[], { timeStart: string; timeEnd: string, downsampleInterval?: number, useDefaultDownsampleInterval?: boolean }, IQueryOptions] => [
      [identifier!],
      {
        timeStart: startDate?.toISOString(),
        timeEnd: endDate?.toISOString(),
        downsampleInterval: advancedProps.downsampleInterval,
        useDefaultDownsampleInterval: true,
      },
      {
        refetchInterval: 0,
        enabled: !!identifier && isDatasetUsage && dataArray.length > 0 && !hasDuplicatedDatasets,
        keepPreviousData: true,
      },
    ],
    [endDate, startDate]
  );

  const { data: baselineDatasetData } = useGetDataInfoRange(
    ...queryParams(advancedProps.baseline?.dataset, advancedProps.baseline?.usage === "dataset")
  );
  const { data: minLineDatasetData } = useGetDataInfoRange(
    ...queryParams(advancedProps.minLine?.dataset, advancedProps.minLine?.usage === "dataset")
  );
  const { data: maxLineDatasetData } = useGetDataInfoRange(
    ...queryParams(advancedProps.maxLine?.dataset, advancedProps.maxLine?.usage === "dataset")
  );

  const getValues = useCallback(
    (config?: LineConfig, data?: InstantDoubleValue[]) => {
      if (config?.usage === "constant") {
        return [
          { x: startDate, y: config.constant ?? 0 },
          { x: endDate, y: config.constant ?? 0 },
        ];
      }
      if (config?.usage === "dataset" && data) {
        return data.map(item => ({
          x: new Date(item.key),
          y: item.value ?? 0,
        }));
      }
      return [];
    },
    [startDate, endDate]
  );

  const baselineValues = useMemo(
    () => getValues(advancedProps.baseline, baselineDatasetData?.[0]?.data),
    [advancedProps.baseline, baselineDatasetData, getValues]
  );
  const minValues = useMemo(
    () => getValues(advancedProps.minLine, minLineDatasetData?.[0]?.data),
    [advancedProps.minLine, getValues, minLineDatasetData]
  );
  const maxValues = useMemo(
    () => getValues(advancedProps.maxLine, maxLineDatasetData?.[0]?.data),
    [advancedProps.maxLine, getValues, maxLineDatasetData]
  );

  useEffect(() => {
    if (dataArray.at(-1)! >= maxValues.at(-1)?.y!) {
      setValueLabel(prev => ({ ...prev, textColor: advancedProps.maxLine?.color }));
    } else if (dataArray.at(-1)! <= minValues.at(-1)?.y!) {
      setValueLabel(prev => ({ ...prev, textColor: advancedProps.minLine?.color }));
    } else {
      setValueLabel(prev => ({ ...prev, textColor: "inherit" }));
    }
  }, [
    advancedProps.maxLine?.color,
    advancedProps.minLine?.color,
    dataArray,
    maxValues,
    minValues,
    setValueLabel,
  ]);

  const baselineDataChart: ChartDataset<"line", Datapoint[]> = {
    data: baselineValues,
    borderColor: advancedProps.baseline?.color,
    borderWidth: advancedProps.baseline?.width,
    pointStyle: false,
  };
  const minValueLine: ChartDataset<"line", Datapoint[]> = {
    data: minValues,
    borderColor: advancedProps.minLine?.color,
    borderWidth: 1,
    borderDash: [2, 5],
    pointStyle: false,
  };
  const maxValueLine: ChartDataset<"line", Datapoint[]> = {
    data: maxValues,
    borderColor: advancedProps.maxLine?.color,
    borderWidth: 1,
    borderDash: [3, 5],
    pointStyle: false,
  };

  const lineOptions = options(advancedProps.showYAxis, minYAxis, maxYAxis);

  const currentChartWidth = chartWidth ? `${chartWidth}px` : "100%";

  const getChartHeight = () => {
    if (isInlineMultipleCharts) {
      return "100%";
    }
    if (advancedProps.isMultipleCharts) {
      return "145px";
    }
    return chartHeight ? `${chartHeight}px` : "100%";
  };

  const style = {
    width: currentChartWidth,
    height: getChartHeight(),
    backgroundColor: advancedProps.isMultipleCharts ? "white" : undefined,
    borderRadius: advancedProps.isMultipleCharts ? "5px" : undefined,
    padding: advancedProps.isMultipleCharts ? "10px 5px" : undefined,
  };

  return (
    <div style={style} className="relative spark-line-chart">
      <Line
        data={{
          datasets: [
            {
              data: advancedProps.data,
              borderColor: "transparent",
              borderWidth: 0,
              pointStyle: false,

              fill: {
                target: 4,
                below: advancedProps.minLine?.color,
                above: "transparent",
              },
            },
            {
              data: advancedProps.data,
              borderColor: "transparent",
              borderWidth: 0,
              pointStyle: false,

              fill: {
                target: 5,
                below: "transparent",
                above: advancedProps.maxLine?.color,
              },
            },
            {
              data: advancedProps.data,
              borderColor: lineColor,
              borderWidth: lineWidth,
              pointStyle: false,

              fill: {
                target: 3,
                above: advancedProps.baseline?.fillAboveColor,
                below: advancedProps.baseline?.fillBelowColor,
              },
            },
            baselineDataChart,
            minValueLine,
            maxValueLine,
          ],
        }}
        options={lineOptions}
        style={{ flex: 2, width: currentChartWidth }}
      />
      {hasDuplicatedDatasets && (
        <div className="error-message">
          {T("error.message.there-are-duplicated-datasets-in-the-config")}
        </div>
      )}
    </div>
  );
};

export default SparkLineChart;
